import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * A line gizmo: a BufferGeometry from flat `positions` and optional `colors`, drawn as an unlit
 * transparent `<lineSegments>` over the scene. A Godot gizmo-plugin gizmo mounts it inside a
 * `PluginGizmo`, which gives it the look of its node. R3F does not dispose an attached geometry, so
 * the gizmo disposes it on rebuild.
 */
import { useMemo } from 'react';
import * as THREE from 'three';
import { materialProgramInputs } from '../materialProgramInputs';
import { useDisposeOnRelease } from '../useDisposeOnRelease';
import { useGizmoLook } from '../gizmo/useGizmoLook';
import { GIZMO_RENDER_ORDER, GizmoGroup } from './GizmoGroup';
const FLOATS_PER_SEGMENT = 6;
/** Whether the segment starting at `start` has an `inf` or `nan` vertex. */
function hasNonFiniteVertex(positions, start) {
    for (let i = start; i < start + FLOATS_PER_SEGMENT; i++) {
        if (!Number.isFinite(positions[i]))
            return true;
    }
    return false;
}
/** The segments that have no `inf` or `nan` vertex. The same arrays when every segment has none. */
function finiteSegments(positions, colors) {
    if (positions.every(Number.isFinite))
        return { positions, colors };
    const segmentCount = Math.floor(positions.length / FLOATS_PER_SEGMENT);
    const keptStarts = [];
    for (let segment = 0; segment < segmentCount; segment++) {
        const start = segment * FLOATS_PER_SEGMENT;
        if (!hasNonFiniteVertex(positions, start))
            keptStarts.push(start);
    }
    const onlyKept = (array) => {
        const kept = new Float32Array(keptStarts.length * FLOATS_PER_SEGMENT);
        keptStarts.forEach((start, index) => kept.set(array.subarray(start, start + FLOATS_PER_SEGMENT), index * FLOATS_PER_SEGMENT));
        return kept;
    };
    return { positions: onlyKept(positions), colors: colors && onlyKept(colors) };
}
/**
 * Draws each segment that has no `inf` or `nan` vertex. Godot draws no pixel for a line with
 * one, and its finite neighbours still draw. three.js warns about a NaN bounding sphere.
 */
export function GizmoLine({ positions: allPositions, colors: allColors, color: ownColor, opacity: ownOpacity = 1, }) {
    const { color, opacity } = useGizmoLook(ownColor ?? 0xffffff, ownOpacity);
    const { positions, colors } = useMemo(() => finiteSegments(allPositions, allColors), [allPositions, allColors]);
    const geometry = useMemo(() => {
        if (positions.length === 0)
            return null;
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        if (colors)
            g.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        return g;
    }, [positions, colors]);
    useDisposeOnRelease(geometry);
    const hasColors = colors !== undefined;
    // `vertexColors` is a program input (r187 `WebGLPrograms.js:319`), and a gizmo that
    // gains or loses its colour attribute would otherwise keep the first program.
    const program = useMemo(() => materialProgramInputs({
        props: {
            color,
            opacity,
            vertexColors: hasColors,
            depthWrite: false,
            transparent: true,
        },
    }), [color, opacity, hasColors]);
    if (!geometry)
        return null;
    return (_jsx(GizmoGroup, { children: _jsxs("lineSegments", { renderOrder: GIZMO_RENDER_ORDER, children: [_jsx("primitive", { object: geometry, attach: "geometry" }), _jsx("lineBasicMaterial", { ...program.props }, program.key)] }) }));
}
//# sourceMappingURL=GizmoLine.js.map